/**
 * Favorites — shared toggle that owns the seller's `favorites` array
 * inside `currentUser.attributes.profile.privateData.favorites`.
 *
 * Authenticated users persist favorites to their profile via
 * `sdk.currentUser.updateProfile`. Anonymous visitors keep them in
 * `localStorage` under `LOCAL_STORAGE_KEY` so the heart icon remains
 * useful pre-signup. When a guest signs in, any locally stored ids
 * are merged into the user's profile (see `syncGuestFavoritesThunk`)
 * and the local cache is cleared.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';

import { setCurrentUser } from './user.duck';

const LOCAL_STORAGE_KEY = 'zp_guest_favorites';

const FAVORITES_PROFILE_FIELDS = {
  expand: true,
  include: ['profileImage'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage;

const readLocalFavorites = () => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(x => typeof x === 'string') : [];
  } catch (e) {
    return [];
  }
};

const writeLocalFavorites = ids => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ids));
  } catch (e) {
    // Quota errors / disabled storage — ignore; in-memory state is still
    // good for this session.
  }
};

const clearLocalFavorites = () => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (e) {
    /* noop */
  }
};

const toListingId = listingId =>
  typeof listingId === 'string' ? listingId : listingId?.uuid;

const getProfileFavorites = state =>
  state?.user?.currentUser?.attributes?.profile?.privateData?.favorites || [];

/**
 * Selector — returns the current user's favorites array. Falls back
 * to the guest cache for unauthenticated visitors so the heart icon
 * stays accurate before signup.
 */
export const selectFavorites = state => {
  const isAuthenticated = !!state?.auth?.isAuthenticated;
  if (isAuthenticated) return getProfileFavorites(state);
  return state?.favorites?.guestFavorites || [];
};

/**
 * Selector — true if the given listing id is in the current user's
 * favorites (profile when logged in, localStorage cache otherwise).
 */
export const selectIsFavorite = (state, listingId) => {
  const id = toListingId(listingId);
  if (!id) return false;
  return selectFavorites(state).includes(id);
};

export const toggleFavoriteThunk = createAsyncThunk(
  'favorites/toggle',
  async (listingId, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    const id = toListingId(listingId);
    if (!id) {
      return rejectWithValue(storableError(new Error('Missing listing id')));
    }

    const state = getState();
    const isAuthenticated = !!state?.auth?.isAuthenticated;

    if (!isAuthenticated) {
      const current = state?.favorites?.guestFavorites || [];
      const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
      writeLocalFavorites(next);
      return { mode: 'guest', favorites: next };
    }

    const current = getProfileFavorites(state);
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    try {
      const response = await sdk.currentUser.updateProfile(
        { privateData: { favorites: next } },
        FAVORITES_PROFILE_FIELDS
      );
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));
      return { mode: 'user', favorites: next };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const toggleFavorite = listingId => dispatch =>
  dispatch(toggleFavoriteThunk(listingId)).unwrap();

/**
 * Merge locally cached guest favorites into the authenticated user's
 * profile. Called from `fetchCurrentUser` once we know the user is
 * signed in; safe no-op when localStorage is empty or already covered
 * by the profile list.
 */
export const syncGuestFavoritesThunk = createAsyncThunk(
  'favorites/syncGuest',
  async (_arg, { getState, dispatch, rejectWithValue, extra: sdk }) => {
    const guest = readLocalFavorites();
    if (!guest.length) {
      return { merged: false };
    }
    const state = getState();
    if (!state?.user?.currentUser) {
      return { merged: false };
    }
    const current = getProfileFavorites(state);
    const merged = Array.from(new Set([...current, ...guest]));
    if (merged.length === current.length) {
      clearLocalFavorites();
      return { merged: false };
    }
    try {
      const response = await sdk.currentUser.updateProfile(
        { privateData: { favorites: merged } },
        FAVORITES_PROFILE_FIELDS
      );
      const entities = denormalisedResponseEntities(response);
      if (entities.length === 1) {
        dispatch(setCurrentUser(entities[0]));
      }
      clearLocalFavorites();
      return { merged: true, favorites: merged };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const syncGuestFavorites = () => dispatch =>
  dispatch(syncGuestFavoritesThunk()).unwrap();

// Max ids the Marketplace API accepts in a single `ids` query.
const MAX_IDS_PER_QUERY = 100;

/**
 * Count the favorites that are still buyable, so the topbar badge matches
 * what the favorites page actually lists (sold items are filtered out
 * there — see FavoriteListingsPage.duck). Deliberately cheap: one request
 * with `perPage: 1` that only reads `meta.totalItems`, no images/includes.
 */
export const fetchAvailableFavoritesCountThunk = createAsyncThunk(
  'favorites/fetchAvailableCount',
  async (_arg, { getState, rejectWithValue, extra: sdk }) => {
    const ids = selectFavorites(getState()).slice(0, MAX_IDS_PER_QUERY);
    if (!ids.length) {
      return 0;
    }
    try {
      const response = await sdk.listings.query({
        ids,
        minStock: 1,
        stockMode: 'match-undefined',
        perPage: 1,
      });
      return response?.data?.meta?.totalItems ?? ids.length;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const fetchAvailableFavoritesCount = () => dispatch =>
  dispatch(fetchAvailableFavoritesCountThunk());

/**
 * Selector — number of favorites to show on the badge. Falls back to the
 * raw saved count until the availability check has resolved, so the badge
 * never renders empty while loading.
 */
export const selectFavoritesBadgeCount = state => {
  const available = state?.favorites?.availableCount;
  return typeof available === 'number' ? available : selectFavorites(state).length;
};

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: {
    guestFavorites: [],
    toggleInProgressId: null,
    toggleError: null,
    // Number of favorites that are still buyable (sold ones excluded), or
    // null until the check resolves. Drives the topbar badge.
    availableCount: null,
  },
  reducers: {
    /**
     * Hydrate guest favorites from localStorage. Dispatched once on
     * client boot so the initial state stays SSR-safe (empty on the
     * server, populated on the client after mount).
     */
    hydrateGuestFavorites: state => {
      state.guestFavorites = readLocalFavorites();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(toggleFavoriteThunk.pending, (state, action) => {
        const arg = action.meta?.arg;
        state.toggleInProgressId = typeof arg === 'string' ? arg : arg?.uuid || null;
        state.toggleError = null;
      })
      .addCase(toggleFavoriteThunk.fulfilled, (state, action) => {
        state.toggleInProgressId = null;
        state.toggleError = null;
        if (action.payload?.mode === 'guest') {
          state.guestFavorites = action.payload.favorites || [];
        }
      })
      .addCase(toggleFavoriteThunk.rejected, (state, action) => {
        state.toggleInProgressId = null;
        state.toggleError = action.payload;
      })
      .addCase(syncGuestFavoritesThunk.fulfilled, state => {
        // Guest cache has been merged into the profile (or was empty);
        // either way the local list is no longer the source of truth.
        state.guestFavorites = [];
      })
      .addCase(fetchAvailableFavoritesCountThunk.fulfilled, (state, action) => {
        state.availableCount = action.payload;
      })
      .addCase(fetchAvailableFavoritesCountThunk.rejected, state => {
        // Leave availableCount as-is; the selector falls back to the raw
        // saved count so the badge still renders something sensible.
        state.availableCount = state.availableCount ?? null;
      });
  },
});

export const { hydrateGuestFavorites } = favoritesSlice.actions;
export default favoritesSlice.reducer;
